import React from "react";
import { BookOpen, Sparkles, Coins, Globe2, Check, Star, ShoppingBag, FileText, Package, Crown, Users, Mic } from "lucide-react";

/**
 * A4 flyer for Fliboe Flip (audiobook platform for indie authors).
 * Designed to render at 1240 × 1754 px so a Playwright screenshot at
 * that viewport produces a print-ready 300 DPI PNG at A4.
 * URL: /flyer/flip — save the PNG, print or drop it into social posts.
 */
export default function BrochureFlip() {
  return (
    <main className="min-h-screen bg-white text-slate-900 font-sans print:m-0" data-testid="flip-flyer">
      <div
        className="mx-auto bg-[#0b0616] text-white overflow-hidden shadow-2xl print:shadow-none"
        style={{ width: "min(100%, 1240px)", aspectRatio: "1240 / 1754" }}
      >
        {/* Hero band */}
        <div className="relative px-10 pt-10 pb-8 bg-gradient-to-br from-violet-900/70 via-black to-fuchsia-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/25 border border-violet-300/40 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-violet-200" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-violet-300 font-mono">// Fliboe Flip · Author platform</div>
              <div className="text-2xl font-black tracking-tight">Fliboe Flip</div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">fliboe.com/publish</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono mt-0.5">est. 2026</div>
            </div>
          </div>

          <h1 className="mt-8 text-[62px] leading-[0.95] font-black tracking-tighter">
            Your book. AI-narrated.
            <br />
            <span className="text-violet-300">Sold on your terms.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-white/80 leading-relaxed">
            Publish an audiobook in 16 languages. Set your own prices for PDF, audio, and bundle. Every dollar of every sale + tip lands in <strong className="text-emerald-300">your</strong> Stripe or Gumroad account — never Fliboe&apos;s.
          </p>

          <div className="mt-6 flex gap-2 flex-wrap">
            {["16 languages", "AI narration", "PDF + audio", "Your Stripe", "No revenue share", "Cancel anytime"].map(c => (
              <span key={c} className="text-[11px] font-mono uppercase tracking-widest px-3 py-1 rounded-full border border-violet-300/30 bg-white/5 text-violet-100">{c}</span>
            ))}
          </div>
        </div>

        {/* Feature grid */}
        <div className="px-10 py-8 grid grid-cols-3 gap-5">
          {[
            { icon: Sparkles, t: "AI-narrated in your voice pick", d: "Four studio voices — Onyx, Nova, Shimmer, Echo. Paste your text, we voice every chapter." },
            { icon: Globe2, t: "16 languages, one click", d: "English, Spanish, Hindi, French, Portuguese, German, Japanese, Mandarin, and 8 more." },
            { icon: FileText, t: "Sell your PDF", d: "Upload once, buyers get the file after payment on Stripe or Gumroad — Fliboe hosts, you deliver." },
            { icon: ShoppingBag, t: "Sell the audiobook", d: "Set a price for the full narration. Paste your buy-link and Fliboe drives the buyers." },
            { icon: Package, t: "Bundle PDF + audio", d: "One price, one click, both formats. Perfect for tipping the balance for on-the-fence readers." },
            { icon: Coins, t: "You keep 100%", d: "Tips + PDF sales + audio sales — every dollar goes to your account. We take zero, ever." },
            { icon: Users, t: "Public author page", d: "Bio, avatar, socials, tip jar and every book you publish, at fliboe.com/author/you." },
            { icon: Star, t: "Reader reviews", d: "1-5 stars, written testimonials, verified-buyer badges — social proof baked in." },
            { icon: Mic, t: "Bundled Pulse SEO blog", d: "Rank your book on Google + ChatGPT with AI-written posts. 4/mo Starter · 20/mo Pro." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
              <Icon className="w-5 h-5 text-violet-300" />
              <div className="mt-3 text-white font-bold text-[15px] leading-snug">{t}</div>
              <div className="mt-1.5 text-white/60 text-[12px] leading-relaxed">{d}</div>
            </div>
          ))}
        </div>

        {/* Plans */}
        <div className="px-10 pt-2 pb-8 grid grid-cols-2 gap-5">
          {/* Starter */}
          <div className="rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-500/10 to-black/40 p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-violet-300">// For your first three books</div>
            <div className="mt-2 text-3xl font-black">Flip Starter</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-5xl font-black">$9.99</span>
              <span className="text-white/50 text-sm">/ month</span>
            </div>
            <ul className="mt-4 space-y-1.5 text-[12.5px] text-white/85">
              <li className="flex gap-2"><Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" /> 3 books · unlimited chapters each</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" /> 4 AI voices · 5 languages (EN, ES, HI, FR, PT)</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" /> Public author page + reviews</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" /> Bundled Pulse blog · 4 SEO posts/mo</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-violet-300 shrink-0 mt-0.5" /> Keep 100% of tips + all sales</li>
            </ul>
          </div>
          {/* Pro */}
          <div className="rounded-2xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-500/10 via-black/40 to-fuchsia-500/10 p-6 relative">
            <div className="absolute -top-3 right-4 bg-emerald-500 text-black text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Crown className="w-3 h-3" /> Most popular
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">// For serial authors going pro</div>
            <div className="mt-2 text-3xl font-black">Flip Pro</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-5xl font-black">$19.99</span>
              <span className="text-white/50 text-sm">/ month</span>
            </div>
            <ul className="mt-4 space-y-1.5 text-[12.5px] text-white/85">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> Unlimited books & chapters</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> All 16 languages · every voice</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> Priority narration + featured placement</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> Advanced analytics · custom author page</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /> Bundled Pulse · 20 posts/mo · 3 sites</li>
            </ul>
          </div>
        </div>

        {/* CTA footer */}
        <div className="px-10 py-6 bg-gradient-to-r from-violet-600 to-fuchsia-600 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="text-white text-2xl font-black tracking-tight">Start publishing today.</div>
            <div className="text-white/85 text-sm mt-1">No manual review. Your book is live the moment narration finishes.</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/70">Sign up now</div>
              <div className="text-white text-lg font-black tracking-tight">fliboe.com/publish</div>
            </div>
            <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center text-black text-[9px] font-mono font-bold text-center leading-tight px-1">
              SCAN<br />OR VISIT<br />fliboe.com/<br />publish
            </div>
          </div>
        </div>

        {/* Footer strip */}
        <div className="px-10 py-3 bg-black flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/50">
          <span>© 2026 Fliboe FM · Author platform</span>
          <span>Cancel anytime · No revenue share · You own your work</span>
        </div>
      </div>

      {/* Print helper */}
      <div className="max-w-3xl mx-auto p-6 text-center print:hidden">
        <button
          onClick={() => window.print()}
          className="px-6 py-2.5 rounded-full bg-violet-600 text-white font-semibold hover:bg-violet-500 transition"
          data-testid="flip-flyer-print"
        >
          Print / Save as PDF
        </button>
        <p className="text-slate-500 text-sm mt-3">Renders as a print-ready A4 page. Also great as a Twitter/Instagram image — take a screenshot at 1240 px wide.</p>
      </div>
    </main>
  );
}
